/**
 * Os dados que a equipe corrige na ficha do cliente (pedido do Vitor, 06/10: "preciso que o pessoal consiga trocar os
 * dados dos usuarios na global"; o caso real: o cliente digitou o e-mail da compra errado, "gmai.com", nao recebeu o
 * link de criar senha e a equipe nao tinha como corrigir). Vale nas duas marcas.
 *
 * E-mails: acrescentar, tornar principal, remover e o principal, "Corrigir e-mail", que faz tudo numa tela so: acrescenta
 * o certo, torna principal, remove o errado e reenvia o acesso, nessa ordem, e para no passo que falhar dizendo o que
 * ficou feito (`corrigirEmail.ts`). Dados pessoais: nome, telefone e nascimento. O CPF nao se edita (e a identidade).
 *
 * A tela so manda e mostra (ADR-001): quem barra e o backend (e-mail de outra conta, principal que nao sai) e a recusa
 * aparece como ele escreveu. A trilha de tudo isto o backend ja grava.
 */
import {
  Aviso, Botao, Campo, CampoCaixa, CampoData, CampoSelecao, Dialogo, EmailParaConferir, ErroApi, ListaDeEmails, Percurso, Secao, Selo,
  emailValido, formatarData, formatarDia, formatarTelefone, sugerirDominioDeEmail, SugestaoDeDominio, type EmailDaLista, type PassoDoPercurso,
} from "@mtc/ui";
import { useState, type FormEvent, type ReactNode } from "react";
import { erroDe, quem, resumoDe, useApi, usePapel } from "../api";
import { TITULO_DO_PASSO, andamentoVazio, correr, fraseDaParada, passosDaCorrecao, type Andamento, type Parada, type Passo } from "../corrigirEmail";
import { DialogoDeAcao } from "./DialogoDeAcao";

export type EmailDaFicha = { id: string; email: string; principal: boolean; verificado: boolean; origem: string; criado_em?: string };
export type AuditoriaDaFicha = { em: string; ator: string; acao: string; antes?: unknown; depois?: unknown };
export type PessoaDaFicha = { id: string; cpf: string; nome: string; telefone: string | null; nascimento?: string | null; origem: string; criado_em: string };
export type AcessoDaFicha = { tem_senha: boolean; senha_definida_em?: string; ultimo_login_em?: string | null; sessoes_ativas: number };

const campo = (x: unknown, k: string): string | undefined => {
  const v = x && typeof x === "object" ? (x as Record<string, unknown>)[k] : undefined;
  return typeof v === "string" ? v : undefined;
};
const normalizado = (s: string) => s.trim().toLowerCase();
const ORIGEM_DO_EMAIL: Record<string, string> = { CHECKOUT: "Veio da compra", RECUPERACAO: "Veio da recuperação de acesso", PERFIL: "O cliente acrescentou", ADMIN: "Acrescentado pela equipe" };

/** O que a trilha da pessoa diz de cada ato, numa linha legivel: o e-mail, de qual pra qual, o que mudou. */
export function detalheDaTrilha(a: AuditoriaDaFicha): string {
  const { acao, antes, depois } = a;
  if (acao === "EMAIL_ADICIONADO") return `${campo(depois, "email") ?? ""}${depois && (depois as { principal?: boolean }).principal ? ", como e-mail de acesso" : ""}`;
  if (acao === "EMAIL_REMOVIDO") return campo(antes, "email") ?? "";
  if (acao === "EMAIL_PRINCIPAL_ALTERADO") return `${campo(antes, "principal") ?? "nenhum"} para ${campo(depois, "principal") ?? ""}`;
  if (acao === "EMAIL_VERIFICADO") return campo(depois, "email") ?? "";
  if (acao === "LINK_DE_ACESSO_REENVIADO") return `para ${campo(depois, "para") ?? ""}`;
  if (acao === "PESSOA_ATUALIZADA") {
    const mudou = ["nome", "telefone", "nascimento"].flatMap((k) => {
      const de = campo(antes, k) ?? null;
      const pra = campo(depois, k) ?? null;
      const mostra = (v: string | null) => (v == null ? "vazio" : k === "nascimento" ? formatarDia(v.slice(0, 10)) : v);
      return de === pra ? [] : [`${k === "nascimento" ? "nascimento" : k}: ${mostra(de)} para ${mostra(pra)}`];
    });
    return mudou.join("; ");
  }
  return resumoDe(depois);
}

/** De onde veio o e-mail e quando; se a trilha sabe, quem acrescentou. */
function apoioDoEmail(e: EmailDaFicha, auditoria: AuditoriaDaFicha[]): string {
  const add = auditoria.find((a) => a.acao === "EMAIL_ADICIONADO" && campo(a.depois, "email") === e.email);
  const origem = e.origem === "ADMIN" && add ? `Acrescentado por ${quem(add.ator)}` : (ORIGEM_DO_EMAIL[e.origem] ?? e.origem);
  return `${origem}${e.criado_em ? ` em ${formatarData(e.criado_em)}` : ""}`;
}

// ---------------------------------------------------------------- a secao

type Aberto = { tipo: "corrigir" | "principal" | "remover"; email: EmailDaFicha } | { tipo: "adicionar" } | null;

export function SecaoDeEmails({ pessoa, emails, acesso, auditoria, aoMudar, aoFeito }: { pessoa: PessoaDaFicha; emails: EmailDaFicha[]; acesso: AcessoDaFicha; auditoria: AuditoriaDaFicha[]; aoMudar: () => Promise<unknown>; aoFeito: (texto: string) => void }) {
  const { pode } = usePapel();
  const [aberto, setAberto] = useState<Aberto>(null);
  const podeMexer = pode("SUPORTE");
  const fechar = () => setAberto(null);
  const itens: EmailDaLista[] = emails.map((e) => ({
    chave: e.id,
    email: e.email,
    principal: e.principal,
    selos: (
      <>
        {e.principal && <Selo tom="info">principal</Selo>}
        {e.verificado ? <Selo tom="sucesso">verificado</Selo> : <Selo tom="alerta">não verificado</Selo>}
      </>
    ),
    detalhe: apoioDoEmail(e, auditoria),
    acoes: podeMexer ? (
      <>
        <Botao variante="secundario" tamanho="pequeno" aria-label={`Corrigir o e-mail ${e.email}`} onClick={() => setAberto({ tipo: "corrigir", email: e })}>Corrigir</Botao>
        {!e.principal && <Botao variante="secundario" tamanho="pequeno" aria-label={`Tornar principal o e-mail ${e.email}`} onClick={() => setAberto({ tipo: "principal", email: e })}>Tornar principal</Botao>}
        {!e.principal && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Remover o e-mail ${e.email}`} onClick={() => setAberto({ tipo: "remover", email: e })}>Remover</Botao>}
      </>
    ) : undefined,
  }));
  return (
    <>
      <Secao titulo="E-mails" acoes={podeMexer ? <Botao tamanho="pequeno" variante="secundario" onClick={() => setAberto({ tipo: "adicionar" })}>Adicionar e-mail</Botao> : undefined}>
        <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>
          O principal é o e-mail de acesso: é pra ele que vai o link de criar senha e os avisos. Cada e-mail pertence a uma conta só.
        </p>
        <ListaDeEmails rotulo="E-mails do cliente" itens={itens} vazio="Sem e-mail. O cliente não consegue entrar nem pedir senha." />
        {!podeMexer && <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Você pode ver os e-mails, mas não mudar.</p>}
      </Secao>

      <AdicionarEmail aberto={aberto?.tipo === "adicionar"} pessoa={pessoa} semEmail={!emails.length} aoFechar={fechar} aoFeito={(t) => { void aoMudar(); aoFeito(t); }} />
      <TornarPrincipal alvo={aberto?.tipo === "principal" ? aberto.email : null} atual={emails.find((e) => e.principal) ?? null} pessoa={pessoa} aoFechar={fechar} aoFeito={(t) => { void aoMudar(); aoFeito(t); }} />
      <RemoverEmail alvo={aberto?.tipo === "remover" ? aberto.email : null} pessoa={pessoa} aoFechar={fechar} aoFeito={(t) => { void aoMudar(); aoFeito(t); }} />
      <CorrigirEmail alvo={aberto?.tipo === "corrigir" ? aberto.email : null} pessoa={pessoa} acesso={acesso} aoFechar={fechar} aoMudar={aoMudar} aoFeito={aoFeito} />
    </>
  );
}

// ---------------------------------------------------------------- adicionar, tornar principal, remover

function AdicionarEmail({ aberto, pessoa, semEmail, aoFechar, aoFeito }: { aberto: boolean; pessoa: PessoaDaFicha; semEmail: boolean; aoFechar: () => void; aoFeito: (t: string) => void }) {
  const { postar } = useApi();
  const [email, setEmail] = useState("");
  const [confirmei, setConfirmei] = useState(false);
  const limpo = normalizado(email);
  const sugestao = emailValido(limpo) ? sugerirDominioDeEmail(limpo) : null;
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Adicionar e-mail"
      descricao={`Mais um e-mail de ${pessoa.nome}. ${semEmail ? "Como o cadastro não tem nenhum, este vira o e-mail de acesso." : "Ele entra como secundário: pra receber o acesso, torne-o principal depois."}`}
      botao="Adicionar e-mail"
      podeEnviar={emailValido(limpo)}
      aoAbrir={() => { setEmail(""); setConfirmei(false); }}
      aoEnviar={() => postar(`/v1/admin/pessoas/${pessoa.id}/emails`, { email: limpo, confirmado_com_a_pessoa: confirmei })}
      aoConcluir={() => aoFeito(`E-mail ${limpo} acrescentado${semEmail ? " como e-mail de acesso" : ""}.`)}
    >
      <Campo rotulo="E-mail" type="email" inputMode="email" autoComplete="off" spellCheck={false} mono valor={email} aoMudar={setEmail} placeholder="nome@exemplo.com" />
      {sugestao && <SugestaoDeDominio sugestao={sugestao} aoUsar={() => setEmail(sugestao)} />}
      <CampoCaixa marcado={confirmei} aoMudar={setConfirmei}>Confirmei este e-mail com o cliente (marca como verificado)</CampoCaixa>
    </DialogoDeAcao>
  );
}

function TornarPrincipal({ alvo, atual, pessoa, aoFechar, aoFeito }: { alvo: EmailDaFicha | null; atual: EmailDaFicha | null; pessoa: PessoaDaFicha; aoFechar: () => void; aoFeito: (t: string) => void }) {
  const { postar } = useApi();
  return (
    <DialogoDeAcao
      aberto={Boolean(alvo)}
      aoFechar={aoFechar}
      titulo="Tornar principal"
      descricao={alvo ? <>O e-mail de acesso de {pessoa.nome} passa de <span className="mtc-mono">{atual?.email ?? "nenhum"}</span> para <span className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{alvo.email}</span>.</> : undefined}
      botao="Tornar principal"
      aoEnviar={() => postar(`/v1/admin/pessoas/${pessoa.id}/emails/${alvo!.id}/principal`)}
      aoConcluir={() => aoFeito(`${alvo!.email} agora é o e-mail de acesso. O cliente foi avisado na área e o e-mail antigo recebeu um aviso.`)}
    >
      <Aviso tipo="info">O cliente é avisado na área e o e-mail antigo recebe um aviso da troca. A senha continua a mesma.</Aviso>
      {alvo && !alvo.verificado && <Aviso tipo="alerta">Este e-mail ainda não foi verificado. Confira com o cliente que ele é dele antes de torná-lo principal.</Aviso>}
    </DialogoDeAcao>
  );
}

function RemoverEmail({ alvo, pessoa, aoFechar, aoFeito }: { alvo: EmailDaFicha | null; pessoa: PessoaDaFicha; aoFechar: () => void; aoFeito: (t: string) => void }) {
  const { apagar } = useApi();
  return (
    <DialogoDeAcao
      aberto={Boolean(alvo)}
      aoFechar={aoFechar}
      titulo="Remover e-mail"
      descricao={alvo ? <>Tirar <span className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{alvo.email}</span> do cadastro de {pessoa.nome}?</> : undefined}
      botao="Remover e-mail"
      variante="perigo"
      aoEnviar={() => apagar(`/v1/admin/pessoas/${pessoa.id}/emails/${alvo!.id}`)}
      aoConcluir={() => aoFeito(`E-mail ${alvo!.email} removido.`)}
    >
      <Aviso tipo="alerta">O cliente deixa de poder usar este e-mail. Dá pra acrescentá-lo de novo depois. O e-mail principal não pode ser removido: torne outro principal antes.</Aviso>
    </DialogoDeAcao>
  );
}

// ---------------------------------------------------------------- corrigir e-mail (o fluxo principal)

const DETALHE_ESPERADO = (passo: Passo, antigo: string, novo: string): string =>
  passo === "adicionar" ? `${novo} entra no cadastro.`
  : passo === "principal" ? "Passa a receber o acesso e os avisos. O cliente é avisado na área e o e-mail errado recebe um aviso da troca."
  : passo === "remover" ? `${antigo} sai do cadastro.`
  : `Link de criar senha, de uso único, para ${novo}.`;

const DETALHE_FEITO = (passo: Passo, antigo: string, novo: string, jaEstava: boolean): string =>
  passo === "adicionar" ? (jaEstava ? `${novo} já estava no cadastro.` : `${novo} entrou no cadastro.`)
  : passo === "principal" ? `${novo} é o e-mail de acesso.`
  : passo === "remover" ? `${antigo} saiu do cadastro.`
  : `Link enviado para ${novo}.`;

/**
 * O estado do dialogo mora no `CorrigirEmailAberto`, que so existe com o dialogo aberto: abrir de novo comeca do zero,
 * sem depender de um efeito que limpe os campos (o efeito chegava depois de quem ja tinha digitado).
 */
function CorrigirEmail(p: { alvo: EmailDaFicha | null; pessoa: PessoaDaFicha; acesso: AcessoDaFicha; aoFechar: () => void; aoMudar: () => Promise<unknown>; aoFeito: (t: string) => void }) {
  if (!p.alvo) return <Dialogo aberto={false} titulo="Corrigir e-mail" aoFechar={p.aoFechar} />;
  return <CorrigirEmailAberto key={p.alvo.id} {...p} alvo={p.alvo} />;
}

function CorrigirEmailAberto({ alvo, pessoa, acesso, aoFechar, aoMudar, aoFeito }: { alvo: EmailDaFicha; pessoa: PessoaDaFicha; acesso: AcessoDaFicha; aoFechar: () => void; aoMudar: () => Promise<unknown>; aoFeito: (t: string) => void }) {
  const { ler, postar, apagar } = useApi();
  const [novo, setNovo] = useState("");
  const [repete, setRepete] = useState("");
  const [confirmei, setConfirmei] = useState(false);
  // Quem ainda nao tem senha so entra pelo link: sem reenviar, o e-mail certo nao resolve nada.
  const [reenviar, setReenviar] = useState(!acesso.tem_senha);
  const [rodando, setRodando] = useState(false);
  const [atual, setAtual] = useState<Passo | null>(null);
  const [andamento, setAndamento] = useState<Andamento>(andamentoVazio());
  const [parada, setParada] = useState<Parada | null>(null);

  const novoLimpo = normalizado(novo);
  const repeteLimpo = normalizado(repete);
  const formatoOk = emailValido(novoLimpo);
  const igualAoAtual = novoLimpo === alvo.email;
  const iguais = Boolean(repeteLimpo) && repeteLimpo === novoLimpo;
  const sugestao = formatoOk && !igualAoAtual ? sugerirDominioDeEmail(novoLimpo) : null;
  const comecou = andamento.feitos.length > 0 || Boolean(parada) || rodando;
  const passos = passosDaCorrecao({ antigoEraPrincipal: alvo.principal, reenviar });
  const pronto = formatoOk && !igualAoAtual && iguais;

  const chamadas = {
    achar: async (email: string) => (await ler<{ emails: EmailDaFicha[] }>(`/v1/admin/pessoas/${pessoa.id}`)).emails.find((e) => e.email === email)?.id ?? null,
    adicionar: async (email: string, confirmado: boolean) => (await postar<{ id: string }>(`/v1/admin/pessoas/${pessoa.id}/emails`, { email, confirmado_com_a_pessoa: confirmado })).id,
    principal: async (id: string) => { await postar(`/v1/admin/pessoas/${pessoa.id}/emails/${id}/principal`); },
    remover: async (id: string) => {
      try { await apagar(`/v1/admin/pessoas/${pessoa.id}/emails/${id}`); }
      // 404: o e-mail errado ja saiu (uma tentativa anterior removeu e a resposta nao voltou). O resultado e o pedido.
      catch (e) { if (!(e instanceof ErroApi && e.status === 404)) throw e; }
    },
    reenviar: async (id: string) => { await postar(`/v1/admin/pessoas/${pessoa.id}/acesso/reenviar`, { email_id: id }); },
  };

  const executar = async () => {
    if (!pronto || rodando) return;
    setRodando(true);
    setParada(null);
    const r = await correr(passos, { email: novoLimpo, confirmadoComACliente: confirmei, antigoId: alvo.id }, chamadas, andamento, erroDe, (a, passoAtual) => { setAndamento({ ...a, feitos: [...a.feitos] }); setAtual(passoAtual); });
    setRodando(false);
    setAndamento(r.andamento);
    // Mesmo parando no meio, a ficha de tras ja reflete o que ficou feito.
    await aoMudar();
    if (r.parou) { setParada(r.parou); return; }
    aoFechar();
    aoFeito(
      `E-mail corrigido: ${alvo.email} saiu e ${novoLimpo} ${alvo.principal ? "é o e-mail de acesso" : "entrou no cadastro"}.${reenviar ? ` O link de criar a senha foi enviado para ${novoLimpo}.` : ""}`,
    );
  };
  const enviar = (ev: FormEvent) => { ev.preventDefault(); void executar(); };

  const estadoDe = (p: Passo): PassoDoPercurso["estado"] => (andamento.feitos.includes(p) ? "feito" : parada?.passo === p ? "erro" : rodando && atual === p ? "atual" : "futuro");
  const percurso: PassoDoPercurso[] = passos.map((p) => {
    const estado = estadoDe(p);
    return {
      chave: p,
      titulo: TITULO_DO_PASSO[p],
      estado,
      detalhe: estado === "feito" ? DETALHE_FEITO(p, alvo.email, novoLimpo, Boolean(andamento.jaEstava)) : estado === "erro" ? parada!.erro : estado === "atual" ? "Em andamento" : pronto || comecou ? DETALHE_ESPERADO(p, alvo.email, novoLimpo) : undefined,
    };
  });

  const rodape: ReactNode = parada ? (
    <>
      <Botao variante="fantasma" onClick={aoFechar}>Fechar</Botao>
      <Botao carregando={rodando} onClick={() => void executar()}>Tentar de novo</Botao>
    </>
  ) : (
    <>
      <Botao variante="fantasma" onClick={aoFechar} disabled={rodando}>Cancelar</Botao>
      <Botao carregando={rodando} disabled={!pronto} onClick={() => void executar()}>Corrigir e-mail</Botao>
    </>
  );

  return (
    <Dialogo
      aberto
      aoFechar={rodando ? () => undefined : aoFechar}
      titulo="Corrigir e-mail"
      descricao={`Troca o e-mail errado de ${pessoa.nome} pelo certo, em ordem: nada é apagado antes de o certo existir.`}
      rodape={rodape}
    >
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        <EmailParaConferir rotulo="E-mail de agora" email={alvo.email} tom="alerta">
          {alvo.principal ? "É o e-mail de acesso." : "Não é o e-mail de acesso."}{" "}{alvo.verificado ? "Está verificado." : "Nunca foi verificado."}
        </EmailParaConferir>
        <Campo rotulo="E-mail certo" type="email" inputMode="email" autoComplete="off" spellCheck={false} mono valor={novo} aoMudar={setNovo} disabled={comecou} placeholder="nome@exemplo.com" erro={novoLimpo && !formatoOk ? "Isso não parece um e-mail." : igualAoAtual ? "É o mesmo e-mail de agora." : undefined} />
        {sugestao && !comecou && <SugestaoDeDominio sugestao={sugestao} aoUsar={() => { setNovo(sugestao); setRepete(sugestao); }} />}
        <Campo rotulo="Digite o e-mail certo de novo" type="email" inputMode="email" autoComplete="off" spellCheck={false} mono valor={repete} aoMudar={setRepete} disabled={comecou} placeholder="nome@exemplo.com" erro={repeteLimpo && !iguais ? "Os dois e-mails não são iguais." : undefined} />
        {pronto && (
          <EmailParaConferir rotulo="O e-mail certo" email={novoLimpo}>
            Leia letra por letra: é pra ele que o cliente vai receber o acesso.
          </EmailParaConferir>
        )}
        <CampoCaixa marcado={confirmei} aoMudar={setConfirmei} disabled={comecou}>Confirmei este e-mail com o cliente (marca como verificado)</CampoCaixa>
        <CampoCaixa marcado={reenviar} aoMudar={setReenviar} disabled={comecou}>
          Reenviar o acesso pro e-mail novo
          {acesso.tem_senha && <span className="mtc-texto-2" style={{ display: "block", fontSize: "var(--t-xs)" }}>O cliente já tem senha. O link serve pra criar outra e derruba as sessões abertas.</span>}
          {!acesso.tem_senha && <span className="mtc-texto-2" style={{ display: "block", fontSize: "var(--t-xs)" }}>O cliente ainda não criou a senha: sem o link ele não entra.</span>}
        </CampoCaixa>
        <Percurso rotulo="O que vai acontecer" passos={percurso} emPe />
        {parada && <Aviso tipo="erro">{fraseDaParada(passos, andamento, parada)}</Aviso>}
        {/* Enter nos campos envia: o botao de verdade mora no rodape, fora do formulario. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialogo>
  );
}

// ---------------------------------------------------------------- reenviar acesso (botao solto)

export function ReenviarAcesso({ pessoa, emails, acesso, aoFeito }: { pessoa: PessoaDaFicha; emails: EmailDaFicha[]; acesso: AcessoDaFicha; aoFeito: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const [aberto, setAberto] = useState(false);
  const [destino, setDestino] = useState("");
  if (!pode("SUPORTE")) return null;
  const principal = emails.find((e) => e.principal) ?? emails[0];
  const escolhido = emails.find((e) => e.id === destino) ?? principal;
  return (
    <>
      <Botao tamanho="pequeno" variante="secundario" onClick={() => { setDestino(principal?.id ?? ""); setAberto(true); }} disabled={!emails.length}>Reenviar acesso</Botao>
      <DialogoDeAcao
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Reenviar acesso"
        descricao={escolhido ? <>Um link novo de criar senha vai para <span className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{escolhido.email}</span>.</> : "Sem e-mail pra receber o link."}
        botao="Reenviar acesso"
        podeEnviar={Boolean(escolhido)}
        aoEnviar={() => postar(`/v1/admin/pessoas/${pessoa.id}/acesso/reenviar`, { email_id: escolhido!.id })}
        aoConcluir={() => aoFeito(`Link de acesso enviado para ${escolhido!.email}.`)}
      >
        {emails.length > 1 && (
          <CampoSelecao rotulo="Enviar para" valor={escolhido?.id ?? ""} aoMudar={setDestino} opcoes={emails.map((e) => ({ valor: e.id, rotulo: `${e.email}${e.principal ? " (principal)" : ""}` }))} />
        )}
        <Aviso tipo="info">O link é pessoal e de uso único. Confira o e-mail antes: se estiver errado, use Corrigir e-mail, que troca e reenvia de uma vez.</Aviso>
        {acesso.tem_senha && <Aviso tipo="alerta">O cliente já tem senha. Quem usar o link cria outra, e as sessões abertas caem.</Aviso>}
      </DialogoDeAcao>
    </>
  );
}

// ---------------------------------------------------------------- dados pessoais

export function EditarDados({ pessoa, aoMudar, aoFeito }: { pessoa: PessoaDaFicha; aoMudar: () => Promise<unknown>; aoFeito: (t: string) => void }) {
  const { mudar } = useApi();
  const { pode } = usePapel();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [nascimento, setNascimento] = useState("");
  if (!pode("ADMIN")) return null;
  const nascimentoSalvo = pessoa.nascimento ? pessoa.nascimento.slice(0, 10) : "";
  // O telefone aparece com a mascara (como no Perfil do cliente): so conta como mudado se mudou depois dela.
  const telefoneSalvo = pessoa.telefone ? formatarTelefone(pessoa.telefone) : "";
  const mudouNome = nome.trim() !== pessoa.nome;
  const mudouTelefone = telefone.trim() !== telefoneSalvo;
  const mudouNascimento = nascimento !== nascimentoSalvo;
  const corpo = {
    ...(mudouNome ? { nome: nome.trim() } : {}),
    ...(mudouTelefone ? { telefone: telefone.trim() || null } : {}),
    ...(mudouNascimento ? { nascimento: nascimento || null } : {}),
  };
  const mudancas = [mudouNome && "nome", mudouTelefone && "telefone", mudouNascimento && "nascimento"].filter(Boolean) as string[];
  return (
    <>
      <Botao tamanho="pequeno" variante="secundario" onClick={() => setAberto(true)}>Editar dados</Botao>
      <DialogoDeAcao
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Editar dados"
        descricao="Nome, telefone e nascimento do cliente. Só o que você mudar é salvo, e fica na trilha com o seu nome. O CPF não se edita."
        botao="Salvar dados"
        podeEnviar={mudancas.length > 0 && nome.trim().length >= 2}
        aoAbrir={() => { setNome(pessoa.nome); setTelefone(telefoneSalvo); setNascimento(nascimentoSalvo); }}
        aoEnviar={() => mudar(`/v1/admin/pessoas/${pessoa.id}`, corpo)}
        aoConcluir={() => { void aoMudar(); aoFeito(`Dados atualizados: ${mudancas.join(", ")}.`); }}
      >
        <Campo rotulo="Nome completo" valor={nome} aoMudar={setNome} autoComplete="off" erro={nome.trim().length > 0 && nome.trim().length < 2 ? "Escreva o nome." : undefined} />
        <Campo rotulo="Telefone" valor={telefone} aoMudar={(v) => setTelefone(v ? formatarTelefone(v) : "")} inputMode="tel" autoComplete="off" mono placeholder="(11) 98765-4321" dica="Em branco, o telefone sai do cadastro." />
        <CampoData rotulo="Data de nascimento" valor={nascimento} aoMudar={setNascimento} dica="Em branco, a data sai do cadastro." />
      </DialogoDeAcao>
    </>
  );
}
